import type { FC } from "react";
import { useBoolean } from "usehooks-ts";

import { groupAccessIdFromGroupId } from "../../../../shared/access-id";

import { PageGroupMember } from "components/materials/page/groups/PageGroupMember";
import {
  DraggablePageGroupUserType,
  DraggableSectionUserType,
} from "components/materials/page/groups/helpers";
import { isUserOnline } from "components/materials/section/helpers";
import { Trans, t } from "i18n/i18n";
import { Card } from "mds/components/Card";
import { ConfirmDeletionDialog } from "mds/components/ConfirmDeletionDialog";
import { DragAndDrop } from "mds/components/DragAndDrop";
import { Tooltip } from "mds/components/Tooltip";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import { CircleInfoIcon, PersonSingleIcon, TrashDeleteIcon } from "mds/icons";
import { useAccessIdHasData } from "sharedb/ShareDBDoc";
import { storeApi } from "store/index";
import {
  determineActivityStatus,
  pageGroupSelectors,
  pageSelectors,
  selectActivityStatus,
} from "store/selectors";
import {
  selectPageGroupUsersByPageGroupId,
  selectReadyUserIdsByPageGroupId,
  selectSortedReadyPageGroupUserIdsByPageGroupId,
} from "store/selectors/page-groups";
import {
  useFullObjectSelector,
  useHasItemsSelector,
  useListSelector,
  useObjectSelector,
} from "store/store-hooks";
import { GroupInteractionModeType } from "utils/page-utils";

interface PageGroupEditCardProps {
  pageGroupId: string;
  number: number;
  totalPageGroups: number;
  pageId: string;
  onRemove?: (studentSectionUserId: string) => void;
  moveSectionUserLocally: (
    item: DraggableSectionUserType | DraggablePageGroupUserType,
    toIndex: number,
    dropContainerId: string,
  ) => void;
  onDropSectionUser: (
    droppedItem: DraggableSectionUserType | DraggablePageGroupUserType,
    toIndex: number,
    sourceContainerId: string,
  ) => void;
  showDeleteButton: boolean;
  hasAssessedSubmissions: boolean;
  isReadOnly?: boolean;
}

export const PageGroupEditCard: FC<PageGroupEditCardProps> = ({
  pageGroupId,
  number,
  totalPageGroups,
  pageId,
  onRemove,
  moveSectionUserLocally,
  onDropSectionUser,
  showDeleteButton,
  hasAssessedSubmissions,
  isReadOnly = false,
}) => {
  const page = useObjectSelector(
    (s) => pageSelectors.selectById(s, pageId),
    ["worksheet_id", "interaction_mode"],
  );
  const pageGroup = useObjectSelector(
    (s) => pageGroupSelectors.selectById(s, pageGroupId),
    ["id", "auto_created", "page_id"],
  );
  const readyPageGroupUserIds = useListSelector((s) =>
    selectSortedReadyPageGroupUserIdsByPageGroupId(s, pageGroupId),
  );
  const readyUserIds = useListSelector((s) => selectReadyUserIdsByPageGroupId(s, pageGroupId));
  const hasPageGroupUsers = useHasItemsSelector((s) =>
    selectPageGroupUsersByPageGroupId(s, pageGroupId),
  );
  const groupAccessId = groupAccessIdFromGroupId(pageGroupId);
  const hasData = useAccessIdHasData(page?.worksheet_id, groupAccessId);
  const userActivity = useFullObjectSelector(selectActivityStatus);
  const totalMemberCount = readyPageGroupUserIds.length;
  const onlineMemberCount = readyUserIds.filter((userId) =>
    isUserOnline(determineActivityStatus(userActivity[userId])),
  ).length;

  const hasNoStudents = !hasPageGroupUsers;
  const isDeleteGroupEnabled = totalPageGroups > 2 && (hasNoStudents || !hasAssessedSubmissions);

  const {
    value: confirmationDialogIsOpen,
    setTrue: openConfirmationDialog,
    setFalse: closeConfirmationDialog,
  } = useBoolean(false);

  const confirmDeleteGroup = () => {
    if (hasData) {
      openConfirmationDialog();
    } else {
      storeApi.page_groups.destroy(pageGroupId);
    }
  };

  if (!page || !pageGroup) {
    return null;
  }

  return (
    <>
      <DragAndDrop
        className="w-full"
        dragItemKind="section_user"
        dropContainerId={pageGroupId}
        droppable={!isReadOnly}
        onDraggedItemHover={moveSectionUserLocally}
      >
        <Card className="flex w-full flex-col flex-wrap" data-testid={`group-card-${pageGroupId}`}>
          <div className="mb-1 flex w-full justify-between">
            <div className="h3 ml-2 flex items-center gap-1 truncate">
              {t("common.group_title", { number })}
              {pageGroup.auto_created && (
                <Tooltip content={t("tooltip.auto_created_group")}>
                  <CircleInfoIcon className="text-orange" size="s" />
                </Tooltip>
              )}
            </div>
            <div className="flex gap-1">
              {totalMemberCount === 0 ? null : (
                <div className="h6 flex items-center justify-center gap-1 px-2 text-black-tint-40">
                  <PersonSingleIcon />
                  {onlineMemberCount === totalMemberCount
                    ? totalMemberCount
                    : t("group_configuration_card.online_member_count", {
                        online: onlineMemberCount,
                        total: totalMemberCount,
                      })}
                </div>
              )}
              {showDeleteButton && (
                <SquareIconButton
                  aria-disabled={!isDeleteGroupEnabled}
                  aria-label={
                    hasAssessedSubmissions
                      ? t("group_configuration_card.tooltip.cannot_delete_assessed")
                      : t("group_configuration_card.tooltip.delete_group")
                  }
                  eventName="Group settings - Delete group"
                  kind="tertiary"
                  size="s"
                  onClick={confirmDeleteGroup}
                >
                  <TrashDeleteIcon />
                </SquareIconButton>
              )}
            </div>
          </div>
          {totalMemberCount === 0 ? (
            <div className="body-s ml-2 mt-1 text-black-tint-55">
              <Trans
                i18nKey={`group_configuration_card.empty_group_tip.${
                  page.interaction_mode as GroupInteractionModeType
                }`}
                t={t}
              >
                Click <b>Distribute</b> or drag students here
              </Trans>
            </div>
          ) : (
            readyPageGroupUserIds.map((pageGroupUserId, index) => (
              <PageGroupMember
                groupPageId={pageGroup.page_id}
                isReadOnly={isReadOnly}
                key={pageGroupUserId}
                order={index}
                pageGroupId={pageGroupId}
                pageGroupUserId={pageGroupUserId}
                pageId={pageId}
                onDropSectionUser={onDropSectionUser}
                onRemove={onRemove}
              />
            ))
          )}
        </Card>
      </DragAndDrop>

      <ConfirmDeletionDialog
        glossaryKey="group"
        instanceName={t("common.group_title", { number })}
        open={confirmationDialogIsOpen}
        onClose={closeConfirmationDialog}
        onDelete={() => storeApi.page_groups.destroy(pageGroupId)}
      >
        <div className="mt-4 border-l-2 border-red pl-4 font-bold">
          {t("confirm_delete_modal.student_content_warning", {
            name: t("common.group_title", { number }),
          })}
        </div>
      </ConfirmDeletionDialog>
    </>
  );
};
